import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Chip,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  parseProductionCSV,
  parseExpiryCSV,
  parseLostCSV,
} from "./utils/parsers";
import { calculateKPIs } from "./utils/calculations";
import { getAgentInsights } from "./services/geminiService";
import Sidebar from "./components/Sidebar";
import Filters from "./components/Filters";
import Dashboard from "./components/Dashboard";
import Leaderboard from "./components/Leaderboard";
import AgentDetail from "./components/AgentDetail";
import RetentionMonitor from "./components/RetentionMonitor";
import SettingsPanel from "./components/SettingsPanel";
/* import Login from "./components/Login"; */
import BrokerInsightPage from "./RinnoviPage";

const DEFAULT_SETTINGS = {
  commissions: {
    nonMotorRate: 0.25,
    motor0Gar: 0.04,
    motor1Gar: 0.05,
    motor2PlusGar: 0.065,
    bonusFlatAuto3Gar: 10,
    bonusScontoNonMotorRate: 0.5,
    bonusSconto3GarFlat: 10,
  },
  targets: {
    mixNonMotor: 1.3,
    garMotorNew: 2.0,
    garMotorRen: 1.1,
    retentionBase: 70,
    retentionSuper: 80,
  },
  bonusPerc: {
    level1: 0.01,
    level2: 0.02,
  },
};

const DEFAULT_USERS = [
  { username: "admin", role: "Admin", password: "password" },
];

const App = () => {
  // Stato dati e metadati inizializzati vuoti
  const [data, setData] = useState({ production: [], expiries: [], lost: [] });
  const [uploadMetadata, setUploadMetadata] = useState({});
  const [currentView, setCurrentView] = useState("dashboard");

  const [currentUser, setCurrentUser] = useState(DEFAULT_USERS[0]);
  const [selectedAgentName, setSelectedAgentName] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem("meritx_settings");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Error parsing settings", e);
      }
    }
    return DEFAULT_SETTINGS;
  });

  const [users, setUsers] = useState(() => {
    const saved = localStorage.getItem("meritx_users");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Error parsing users", e);
      }
    }
    return DEFAULT_USERS;
  });

  // Gestione sessione utente
  useEffect(() => {
    const savedUser = localStorage.getItem("meritx_session");
    if (savedUser) {
      try {
        setCurrentUser(JSON.parse(savedUser));
      } catch (e) {
        console.error("Error parsing session", e);
      }
    }
  }, []);

  // CARICAMENTO PERSISTENTE (Associato a User ID)
  useEffect(() => {
    if (currentUser) {
      const storageKey = `meritx_userdata_v3_${currentUser.username}`;
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Sanitizzazione campi calcolati per forzare ricalcolo dinamico
          const cleanProduction = (parsed.data?.production || []).map((p) => ({
            ...p,
            bonusSconto: 0,
            commissionValue: 0,
            commissionRate: 0,
          }));
          setData({
            production: cleanProduction,
            expiries: parsed.data?.expiries || [],
            lost: parsed.data?.lost || [],
          });
          setUploadMetadata(parsed.metadata || {});
        } catch (e) {
          console.error("Errore nel ripristino dati utente", e);
        }
      } else {
        setData({ production: [], expiries: [], lost: [] });
        setUploadMetadata({});
      }
    }
  }, [currentUser]);

  // SALVATAGGIO AUTOMATICO (Persistent Storage)
  useEffect(() => {
    if (
      currentUser &&
      (data.production.length > 0 ||
        data.expiries.length > 0 ||
        data.lost.length > 0)
    ) {
      const storageKey = `meritx_userdata_v3_${currentUser.username}`;
      localStorage.setItem(
        storageKey,
        JSON.stringify({ data, metadata: uploadMetadata }),
      );
    }
  }, [data, uploadMetadata, currentUser]);

  useEffect(() => {
    localStorage.setItem("meritx_settings", JSON.stringify(settings));
    localStorage.setItem("meritx_users", JSON.stringify(users));
  }, [settings, users]);

  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    collaborators: [],
    actionTypes: [],
    products: [],
    excludeStar: false,
    collaboratorType: "Tutti",
  });

  useEffect(() => {
    if (
      currentUser?.role === "Collaborator" &&
      currentUser.linkedCollaborator
    ) {
      setFilters((prev) => ({
        ...prev,
        collaborators: [currentUser.linkedCollaborator],
      }));
      setSelectedAgentName(currentUser.linkedCollaborator);
    }
  }, [currentUser]);

  const handleFileUpload = async (type, file) => {
    const text = await file.text();
    const timestamp = new Date().toLocaleString("it-IT");

    setData((prev) => {
      let newDataRows;
      if (type === "production") newDataRows = parseProductionCSV(text);
      else if (type === "expiries") newDataRows = parseExpiryCSV(text);
      else newDataRows = parseLostCSV(text);

      return { ...prev, [type]: newDataRows };
    });

    setUploadMetadata((prev) => ({
      ...prev,
      [type]: {
        name: file.name, // Mantenuto per compatibilità UI (Sidebar)
        timestamp: timestamp, // Mantenuto per compatibilità UI (Sidebar)
        file_type: type,
        user_id: currentUser?.username || "admin",
      },
    }));
  };

  const availableCollaborators = useMemo(
    () =>
      Array.from(new Set(data.production.map((p) => p.collaboratore))).sort(),
    [data.production],
  );

  const availableProducts = useMemo(
    () => Array.from(new Set(data.production.map((p) => p.prodotto))).sort(),
    [data.production],
  );

  const availableActions = useMemo(
    () => [
      "Emissione annuale",
      "Emissione mensile",
      "Emissione semestrale",
      "Interruzione",
      "Rata mensile",
      "Rata semestrale",
      "Recesso",
      "Sost. IN",
      "Sost. OUT",
    ],
    [],
  );

  const filteredProduction = useMemo(() => {
    return data.production.filter((p) => {
      if (filters.startDate && p.dataTransazione < filters.startDate)
        return false;
      if (filters.endDate && p.dataTransazione > filters.endDate) return false;
      if (filters.excludeStar && p.codicePolizza.includes("*")) return false;
      if (filters.products.length > 0 && !filters.products.includes(p.prodotto))
        return false;
      if (
        filters.actionTypes.length > 0 &&
        !filters.actionTypes.includes(p.tipoAzione)
      )
        return false;
      if (
        filters.collaborators.length > 0 &&
        !filters.collaborators.includes(p.collaboratore)
      )
        return false;
      return true;
    });
  }, [data.production, filters]);

  const filteredExpiries = useMemo(() => {
    return data.expiries.filter((e) => {
      if (
        filters.collaborators.length > 0 &&
        !filters.collaborators.includes(e.collaboratore)
      )
        return false;
      if (filters.excludeStar && e.riferimento.includes("*")) return false;
      return true;
    });
  }, [data.expiries, filters.collaborators, filters.excludeStar]);

  const kpis = useMemo(() => {
    if (filteredProduction.length === 0 && filteredExpiries.length === 0)
      return [];
    return calculateKPIs(filteredProduction, filteredExpiries, settings);
  }, [filteredProduction, filteredExpiries, settings]);

  const selectedAgent = useMemo(() => {
    return kpis.find((k) => k.name === selectedAgentName) || null;
  }, [kpis, selectedAgentName]);

  useEffect(() => {
    if (selectedAgent) {
      setAiLoading(true);
      getAgentInsights(selectedAgent)
        .then((res) => {
          setAiInsights(res);
          setAiLoading(false);
        })
        .catch((err) => {
          console.error("Failed to get insights:", err);
          setAiInsights("Errore nel caricamento degli approfondimenti.");
          setAiLoading(false);
        });
    } else {
      setAiInsights(null);
    }
  }, [selectedAgent]);

  const agencyMetrics = useMemo(() => {
    if (data.production.length === 0)
      return {
        totalVolume: 0,
        avgRetention: 0,
        totalNetNonMotor: 0,
        totalPolizze: 0,
        crossSellPerc: 0,
        totalBonusSconto: 0,
        avgGarGlobal: 0,
        avgGarNew: 0,
        avgGarRen: 0,
        debug: {
          total: 0,
          postF1: 0,
          postF2: 0,
          postF3: 0,
          countRischio: 0,
          countRinnovo: 0,
        },
      };

    // --- STANDARD KPIs ---
    const totalVolume = kpis.reduce(
      (sum, k) => sum + (k.nettoMotor + k.nettoNonMotor),
      0,
    );
    const totalNetNonMotor = kpis.reduce((sum, k) => sum + k.nettoNonMotor, 0);
    const totalPolizze = kpis.reduce((sum, k) => sum + k.polizzeStrict, 0);
    const totalBonusSconto = kpis.reduce(
      (sum, k) => sum + k.totalBonusSconto,
      0,
    );

    const validRetention = kpis.filter((k) => k.retentionRate > 0);
    const avgRetention =
      validRetention.length > 0
        ? validRetention.reduce((sum, k) => sum + k.retentionRate, 0) /
          validRetention.length
        : 0;
    const crossSellPerc =
      totalVolume > 0 ? (totalNetNonMotor / totalVolume) * 100 : 0;

    // --- CALCOLO MEDIA GARANZIE (STRICT "IRON GATE" LOGIC) ---
    // Usiamo lo stesso identico calcolo di calculations.ts ma sull'intero dataset per i KPI globali
    // Questo garantisce che i KPI della dashboard corrispondano esattamente alla media dei collaboratori

    const rawRows = data.production;

    const f1Rows = rawRows.filter((p) => {
      const prod = (p.prodotto || "").trim().toUpperCase();
      return ["AUTO", "MOTO", "VAN"].includes(prod);
    });

    const f2Rows = f1Rows.filter((p) => {
      const code = String(p.codicePolizza || "");
      return !code.includes("*");
    });

    const allowedActions = [
      "EMISSIONE ANNUALE",
      "EMISSIONE MENSILE",
      "EMISSIONE SEMESTRALE",
    ];
    const f3Rows = f2Rows.filter((p) => {
      const action = (p.tipoAzione || "").trim().toUpperCase();
      return allowedActions.includes(action);
    });

    const validSet = f3Rows;

    const totalGar = validSet.reduce(
      (acc, p) => acc + (Number(p.numeroGaranzie) || 0),
      0,
    );
    const avgGarGlobal = validSet.length > 0 ? totalGar / validSet.length : 0;

    const setRischio = validSet.filter((p) => {
      const r = (p.rischio || "").trim().toUpperCase();
      return r === "RISCHIO" || r === "NUOVA POLIZZA";
    });
    const totalGarRischio = setRischio.reduce(
      (acc, p) => acc + (Number(p.numeroGaranzie) || 0),
      0,
    );
    const avgGarNew =
      setRischio.length > 0 ? totalGarRischio / setRischio.length : 0;

    const setRinnovo = validSet.filter(
      (p) => (p.rischio || "").trim().toUpperCase() === "RINNOVO",
    );
    const totalGarRinnovo = setRinnovo.reduce(
      (acc, p) => acc + (Number(p.numeroGaranzie) || 0),
      0,
    );
    const avgGarRen =
      setRinnovo.length > 0 ? totalGarRinnovo / setRinnovo.length : 0;

    return {
      totalVolume,
      avgRetention,
      totalNetNonMotor,
      totalPolizze,
      crossSellPerc,
      totalBonusSconto,
      avgGarGlobal,
      avgGarNew,
      avgGarRen,
      debug: {
        total: rawRows.length,
        postF1: f1Rows.length,
        postF2: f2Rows.length,
        postF3: f3Rows.length,
        countRischio: setRischio.length,
        countRinnovo: setRinnovo.length,
      },
    };
  }, [kpis, data.production]);

  const monitorData = useMemo(
    () => ({
      production: data.production,
      expiries: filteredExpiries,
      lost: data.lost,
    }),
    [data.production, filteredExpiries, data.lost],
  );

  const handleLogin = (user) => {
    setCurrentUser(user);
    localStorage.setItem("meritx_session", JSON.stringify(user));
  };

  const handleLogout = () => {
    setCurrentUser(null);
    localStorage.removeItem("meritx_session");
    setIsSettingsOpen(false);
    setData({ production: [], expiries: [], lost: [] });
    setUploadMetadata({});
  };

  const handleRestoreDefaults = () => {
    if (
      window.confirm("Sei sicuro di voler ripristinare i parametri di default?")
    ) {
      setSettings(DEFAULT_SETTINGS);
    }
  };

  /*  if (!currentUser) {
    return <Login users={users} onLogin={handleLogin} />;
  } */

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        minHeight: "100vh",
      }}
    >
      <Box
        sx={{
          width: { xs: "100%", md: 384 },
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
        }}
      >
        <Sidebar
          onUpload={handleFileUpload}
          fileCounts={{
            prod: data.production.length,
            exp: data.expiries.length,
            lost: data.lost.length,
          }}
          metadata={uploadMetadata}
          onLogout={handleLogout}
          onOpenSettings={() => setIsSettingsOpen(true)}
          role={currentUser.role}
          currentView={currentView}
          onViewChange={setCurrentView}
        />

        <Box
          sx={{
            p: 2,
            overflowY: "auto",
            flexGrow: 1,
          }}
        >
          <Filters
            filters={filters}
            setFilters={setFilters}
            availableCollaborators={availableCollaborators}
            availableProducts={availableProducts}
            availableActions={availableActions}
            locked={currentUser.role === "Collaborator"}
          />

          <Box
            sx={{ mt: 4, pt: 4, borderTop: 1, borderColor: "secondary.light" }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 900,
                  color: "text.secondary",
                  letterSpacing: "0.1em",
                }}
              >
                Analisi AI Mobility
              </Typography>
              <AutoAwesomeIcon
                sx={{
                  color: "primary.main",
                  fontSize: "1rem",
                  animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                }}
              />
            </Box>

            {!selectedAgent ? (
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: "1px dashed",
                  borderColor: "secondary.light",
                  textAlign: "center",
                }}
                elevation={0}
              >
                <Typography
                  sx={{
                    fontSize: "0.625rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    letterSpacing: "-0.02em",
                  }}
                >
                  Seleziona un collaboratore per l'analisi
                </Typography>
              </Paper>
            ) : aiLoading ? (
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: 1,
                  borderColor: "secondary.light",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 1.5,
                }}
                elevation={0}
              >
                <CircularProgress size={24} color="primary" />
                <Typography
                  sx={{
                    fontSize: "0.625rem",
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Generazione insight...
                </Typography>
              </Paper>
            ) : (
              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  border: 1,
                  borderColor: "rgba(234, 88, 12, 0.2)",
                  boxShadow: 3,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.625rem",
                      fontWeight: 900,
                      textTransform: "uppercase",
                      boxShadow: 2,
                    }}
                  >
                    {selectedAgent.name.charAt(0)}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 900,
                      textTransform: "uppercase",
                      letterSpacing: "-0.05em",
                    }}
                  >
                    {selectedAgent.name}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: "0.6875rem",
                    lineHeight: 1.6,
                    whiteSpace: "pre-wrap",
                    fontWeight: 500,
                  }}
                >
                  {aiInsights}
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        component="main"
        sx={{ flex: 1, p: { xs: 2, md: 4 }, overflowY: "auto" }}
      >
        <Box
          component="header"
          sx={{
            mb: 4,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h3" sx={{ color: "text.primary" }}>
              Mobility{" "}
              <Box component="span" sx={{ color: "primary.main" }}>
                MeritX
              </Box>{" "}
              <Box
                component="span"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                Engine Pro
              </Box>
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                fontWeight: "bold",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                fontSize: "0.625rem",
                mt: 0.5,
              }}
            >
              Logged as:{" "}
              <Box
                component="span"
                sx={{ color: "text.primary", fontWeight: 900 }}
              >
                {currentUser.username}
              </Box>{" "}
              ({currentUser.role})
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Paper
              sx={{
                px: 2.5,
                py: 1.25,
                borderRadius: 4,
                boxShadow: 1,
                fontSize: "0.625rem",
                fontWeight: 900,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  bgcolor: "success.main",
                  borderRadius: "50%",
                  animation: "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                }}
              />
              System Status:{" "}
              <Box component="span" sx={{ color: "primary.main" }}>
                Live Analytics
              </Box>
            </Paper>
          </Box>
        </Box>

        {isSettingsOpen ? (
          <Box sx={{ animation: "fadeIn 0.5s ease-out" }}>
            <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-start" }}>
              <Button
                onClick={() => setIsSettingsOpen(false)}
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 900,
                  color: "text.secondary",
                  "&:hover": { color: "text.primary" },
                }}
                startIcon={<ArrowBackIcon />}
              >
                Torna alla Dashboard
              </Button>
            </Box>
            <SettingsPanel
              settings={settings}
              onSaveSettings={setSettings}
              onRestoreDefaults={handleRestoreDefaults}
              users={users}
              onSaveUsers={setUsers}
              availableCollaborators={availableCollaborators}
            />
          </Box>
        ) : currentView === "brokerinsight" ? (
          <Box sx={{ animation: "fadeIn 0.5s ease-out", width: "100%" }}>
            <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-start" }}>
              <Button
                onClick={() => setCurrentView("dashboard")}
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 900,
                  color: "text.secondary",
                  "&:hover": { color: "text.primary" },
                }}
                startIcon={<ArrowBackIcon />}
              >
                Torna a MeritocraticX
              </Button>
            </Box>
            <BrokerInsightPage currentUser={currentUser} />
          </Box>
        ) : data.production.length === 0 && data.expiries.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "60vh",
              border: "4px dashed",
              borderColor: "grey.200",
              borderRadius: 10,
              p: 6,
              textAlign: "center",
            }}
          >
            <Paper
              sx={{
                width: 96,
                height: 96,
                borderRadius: 6,
                boxShadow: 3,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 4,
                transition: "transform 0.5s",
                "&:hover": { transform: "scale(1.1)" },
              }}
            >
              <UploadFileIcon
                sx={{ fontSize: "3rem", color: "primary.light" }}
              />
            </Paper>
            <Typography variant="h4" sx={{ color: "secondary.light" }}>
              Nessun dato caricato
            </Typography>
            <Typography
              sx={{
                color: "text.secondary",
                mt: 1.5,
                maxWidth: 384,
                fontWeight: 500,
                textTransform: "uppercase",
                fontSize: "0.625rem",
                letterSpacing: "0.1em",
              }}
            >
              Carica i report di produzione e scadenze (CSV) per iniziare
              l'analisi meritocratica.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <Dashboard metrics={agencyMetrics} />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <Leaderboard
                kpis={kpis}
                onSelectAgent={(name) => {
                  if (currentUser.role === "Admin") setSelectedAgentName(name);
                }}
                selectedAgentName={selectedAgentName}
              />

              <Box>
                <Box>
                  <RetentionMonitor rawData={monitorData} />
                </Box>
              </Box>
              <Box sx={{ gridColumn: { lg: "span 1" } }}>
                <AgentDetail
                  agent={selectedAgent}
                  rawData={monitorData}
                  aiInsights={aiInsights}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default App;
